import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { parseCsv, cleanCsvField } from '../../utils/csv';
import { canWrite } from '../../utils/roles';
import { clientDisplayName } from '../../utils/clientName';

function normalize(str) {
  return str?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') || '';
}

// CSV de Odoo (res.partner): "Nombre completo","Referencia","Correo electrónico"
function parseClientsCsv(text) {
  const [, ...dataRows] = parseCsv(text);
  const emailRe = /[^\s,]+@[^\s,]+\.[^\s,]+/;
  return dataRows.map(([name, reference, emails]) => {
    const cleanName = cleanCsvField(name);
    const email = emailRe.exec(emails || '')?.[0] || '';
    const company = reference && cleanCsvField(reference) !== cleanName ? cleanCsvField(reference) : '';
    return { name: cleanName, company, email };
  }).filter(r => r.name);
}

function ReadRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13, lineHeight: 1.4 }}>
      <span style={{ color: 'var(--text-dim)', minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ color: 'var(--text)' }}>{value}</span>
    </div>
  );
}

function ClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client?.name || '',
    company: client?.company || '',
    email: client?.email || '',
    phone: client?.phone || '',
    notes: client?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { setError('El nombre es requerido'); return; }
    setSaving(true);
    setError('');
    try {
      const { data } = client
        ? await api.put(`/clients/${client.id}`, form)
        : await api.post('/clients', form);
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{client ? 'Editar cliente' : 'Nuevo cliente'}</h3>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Nombre *</label>
              <input className="form-input" value={form.name} onChange={set('name')} placeholder="Nombre del cliente" autoFocus spellCheck={false} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Empresa</label>
              <input className="form-input" value={form.company} onChange={set('company')} placeholder="Nombre de la empresa" spellCheck={false} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="email@ejemplo.com" />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Teléfono</label>
                <input className="form-input" value={form.phone} onChange={set('phone')} placeholder="+598 99 000 000" />
              </div>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Notas</label>
              <textarea className="form-input" value={form.notes} onChange={set('notes')} placeholder="Observaciones..." rows={3} style={{ resize: 'vertical' }} />
            </div>
            {client && (client.vat || client.mobile || client.address || client.tags || client.odoo_ref || client.job_title) && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Datos de Odoo (sincronizados)</div>
                {client.vat && <ReadRow label="RUT / ID fiscal" value={client.vat} />}
                {client.job_title && <ReadRow label="Puesto" value={client.job_title} />}
                {client.mobile && <ReadRow label="Móvil" value={client.mobile} />}
                {client.address && <ReadRow label="Dirección" value={client.address} />}
                {client.tags && <ReadRow label="Etiquetas" value={client.tags} />}
                {client.odoo_ref && <ReadRow label="Referencia" value={client.odoo_ref} />}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : client ? 'Guardar cambios' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ClientRow({ client, isAdmin, onEdit, onToggle }) {
  const display = clientDisplayName(client);
  const meta = [client.name !== display ? client.name : null, client.vat, client.email, client.phone || client.mobile].filter(Boolean).join(' · ');
  return (
    <div className={`vlist-row${client.active ? '' : ' inactive'}`}>
      <span className="vlist-name">{display}</span>
      <span className="vlist-meta">{meta}</span>
      {isAdmin && (
        <div className="vlist-actions">
          <button className="btn btn-icon btn-ghost" title="Editar" onClick={() => onEdit(client)}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <button className="btn btn-icon btn-ghost" title={client.active ? 'Desactivar' : 'Activar'} onClick={() => onToggle(client)}>
            {client.active ? (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            ) : (
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ClientsPage() {
  const { user } = useAuth();
  const isAdmin = canWrite(user, 'ventas.clientes');

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const fileInputRef = useRef(null);

  const loadSyncStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/settings/clients_last_sync');
      setSyncInfo(data.value ? JSON.parse(data.value) : null);
    } catch { /* informativo, no debe romper la lista */ }
  }, []);
  useEffect(() => {
    loadSyncStatus();
    const t = setInterval(loadSyncStatus, 60000);
    return () => clearInterval(t);
  }, [loadSyncStatus]);

  const handleSyncOdoo = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { data } = await api.post('/clients/sync-odoo');
      alert(`Sincronizado con Odoo · ${data.fetched} contactos (nuevos: ${data.inserted}, actualizados: ${data.updated}, archivados: ${data.archived})`);
      await load();
      await loadSyncStatus();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo sincronizar con Odoo');
    } finally {
      setSyncing(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients${showInactive ? '?all=1' : ''}`);
      setClients(Array.isArray(data) ? data : []);
    } catch {
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const filtered = clients.filter(c => {
    const q = normalize(search);
    return !q || normalize(c.name).includes(q) || normalize(c.company).includes(q) || normalize(c.email).includes(q);
  });

  const handleSaved = saved => {
    setClients(prev => {
      const idx = prev.findIndex(c => c.id === saved.id);
      const next = idx >= 0 ? prev.map((c, i) => i === idx ? saved : c) : [...prev, saved];
      return next.sort((a, b) => clientDisplayName(a).localeCompare(clientDisplayName(b), 'es'));
    });
    setShowModal(false);
    setEditing(null);
  };

  const handleToggle = async client => {
    try {
      const { data } = await api.put(`/clients/${client.id}`, { active: !client.active });
      handleSaved(data);
    } catch {}
  };

  const handleImportFile = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const rows = parseClientsCsv(String(reader.result));
        if (rows.length === 0) { alert('No se encontraron filas válidas en el archivo'); return; }
        const { data } = await api.post('/clients/import', { rows });
        alert(`Importados: ${data.imported} · Ya existían: ${data.skipped} · Total en archivo: ${data.total}`);
        load();
      } catch (err) {
        alert(err.response?.data?.error || 'Error al importar el archivo');
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Clientes</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {syncInfo?.at && (() => {
            const min = Math.max(0, Math.round((Date.now() - new Date(syncInfo.at).getTime()) / 60000));
            const txt = min < 1 ? 'recién' : min < 60 ? `hace ${min} min` : min < 1440 ? `hace ${Math.round(min / 60)} h` : `hace ${Math.round(min / 1440)} d`;
            return (
              <span
                title={syncInfo.error || (syncInfo.count ? `${syncInfo.count} contactos de Odoo` : '')}
                style={{ fontSize: 12, color: syncInfo.ok ? 'var(--text-dim)' : 'var(--red, #ef4444)' }}
              >
                {syncInfo.ok ? `Odoo · actualizado ${txt}` : `Odoo · error ${txt}`}
              </span>
            );
          })()}
          {isAdmin && (
            <>
              <button className="btn btn-ghost" onClick={handleSyncOdoo} disabled={syncing}>
                {syncing ? 'Sincronizando…' : 'Sincronizar con Odoo'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
              <button className="btn btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={importing}>
                {importing ? 'Importando…' : 'Importar CSV'}
              </button>
              <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
                + Nuevo
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 200, maxWidth: 360 }}
          placeholder="Buscar por nombre, empresa o email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Mostrar inactivos
          </label>
        )}
        <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          {filtered.length} {filtered.length === 1 ? 'cliente' : 'clientes'}
        </span>
      </div>

      <div className="vlist">
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-dim)' }}>
            {search ? 'Sin resultados.' : 'No hay clientes registrados.'}
          </div>
        ) : (
          filtered.map(c => (
            <ClientRow
              key={c.id}
              client={c}
              isAdmin={isAdmin}
              onEdit={cl => { setEditing(cl); setShowModal(true); }}
              onToggle={handleToggle}
            />
          ))
        )}
      </div>

      {showModal && (
        <ClientModal
          client={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
